import { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyState, ErrorState, LoadingState, formatDate, formatDateTime } from "../components/AdminPageStates";
import { fetchUserDetail, SimpleRecord, UserDetail as UserDetailModel } from "../services/userDirectoryService";

function DetailSection({
  title,
  emptyMessage,
  children,
  hasData
}: {
  title: string;
  emptyMessage: string;
  children: ReactNode;
  hasData: boolean;
}) {
  return (
    <section className="detail-section">
      <h2>{title}</h2>
      {hasData ? children : <p className="empty-inline">{emptyMessage}</p>}
    </section>
  );
}

function SimpleRecordList({ records }: { records: SimpleRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {records.map((record) => (
        <section className="admin-list-row" key={record.id}>
          <div>
            <p className="row-title">{record.title}</p>
            <p className="row-meta">{record.subtitle}</p>
          </div>
          <p className="row-meta">{formatDate(record.date)}</p>
        </section>
      ))}
    </div>
  );
}

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetailModel | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadDetail() {
      if (!userId) {
        setError("Missing userId.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        setDetail(await fetchUserDetail(userId));
      } catch {
        setError("Unable to load user detail. Check Firebase permissions and custom claims.");
      } finally {
        setIsLoading(false);
      }
    }

    void loadDetail();
  }, [userId]);

  if (isLoading) {
    return <LoadingState message="Loading user detail." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!detail?.user) {
    return <EmptyState message="User profile was not found." />;
  }

  const { user } = detail;
  const roles = user.roles.length > 0 ? user.roles.join(", ") : user.role ?? "Unavailable";

  return (
    <>
      <button type="button" className="secondary-button back-button" onClick={() => navigate("/users")}>
        Back to users
      </button>

      <section className="profile-summary">
        <div>
          <p className="eyebrow">Profile summary</p>
          <h2>{user.displayName ?? user.username ?? "Unnamed user"}</h2>
          <p>{user.email ?? "Email unavailable"}</p>
        </div>
        <div className="summary-grid">
          <span>User ID: {user.id}</span>
          <span>Role(s): {roles}</span>
          <span>Created: {formatDate(user.createdAt)}</span>
          <span>Last active: {formatDate(user.lastSeenAt)}</span>
          <span>Guardian/minor: {user.guardianMinorStatus}</span>
          <span>Status: {user.profileStatus}</span>
        </div>
      </section>

      <DetailSection title="Goal / Path Data" emptyMessage="No goals or journey data available." hasData={detail.goals.goals.length > 0 || detail.goals.guidedJourneyItems.length > 0 || detail.goals.nextMoveRecommendationCount !== null}>
        <>
          {detail.goals.goals.length > 0 ? <p className="detail-text">{detail.goals.goals.join(", ")}</p> : null}
          {detail.goals.nextMoveRecommendationCount !== null ? (
            <p className="detail-text">Next-move recommendations: {detail.goals.nextMoveRecommendationCount}</p>
          ) : null}
          <SimpleRecordList records={detail.goals.guidedJourneyItems} />
        </>
      </DetailSection>

      <DetailSection title="Recent Activity" emptyMessage="No recent activity available." hasData={detail.activity.length > 0}>
        <div className="admin-list compact-list">
          {detail.activity.map((item) => (
            <section className="admin-list-row" key={item.id}>
              <div>
                <p className="row-title">{item.eventName ?? item.eventType ?? "Activity"}</p>
                <p className="row-meta">{item.eventType ?? "No event type"}</p>
              </div>
              <p className="row-meta">{formatDateTime(item.createdAt)}</p>
            </section>
          ))}
        </div>
      </DetailSection>

      <DetailSection title="Skills / Routines" emptyMessage="No skills or routine summaries available." hasData={detail.skills.length > 0 || detail.routines.length > 0}>
        <>
          <SimpleRecordList records={detail.skills} />
          <SimpleRecordList records={detail.routines} />
        </>
      </DetailSection>

      <DetailSection title="Shout-Outs" emptyMessage="No shout-outs available." hasData={detail.shoutOutsReceived.length > 0 || detail.shoutOutsGiven.length > 0}>
        <>
          <p className="detail-text">Received: {detail.shoutOutsReceived.length}</p>
          <p className="detail-text">Given: {detail.shoutOutsGiven.length}</p>
        </>
      </DetailSection>

      <DetailSection title="Payments / Bookings" emptyMessage="No payments or bookings available." hasData={detail.payments.length > 0 || detail.bookings.length > 0}>
        <div className="admin-list compact-list">
          {detail.payments.map((payment) => (
            <section className="admin-list-row" key={`payment-${payment.id}`}>
              <div>
                <p className="row-title">{payment.status ?? "Payment"}</p>
                <p className="row-meta">Gross USD: {payment.grossAmountUSD ?? "Unavailable"}</p>
              </div>
              <p className="row-meta">{formatDate(payment.updatedAt)}</p>
            </section>
          ))}
          {detail.bookings.map((booking) => (
            <section className="admin-list-row" key={`booking-${booking.id}`}>
              <div>
                <p className="row-title">{booking.status ?? "Booking"}</p>
                <p className="row-meta">{booking.coachUserId ?? booking.providerUserId ?? "No coach/provider id"}</p>
              </div>
              <p className="row-meta">{formatDate(booking.updatedAt)}</p>
            </section>
          ))}
        </div>
      </DetailSection>
    </>
  );
}
